import { Session } from '@graphql/gql/graphql';
import React, { createContext, useState, useContext, useMemo, ReactNode } from 'react';

export type UpdateSession = {
  session: {
    id: string;
    enrolled: boolean;
    capacity?: {
      availableCapacity?: number;
    };
  };
  enrolling: boolean;
};
export type EnrolledSessionsContextType = {
  enrolledSessions: Session[];
  setEnrollment: (sessions: UpdateSession[]) => void;
};

const EnrolledSessionsContext = createContext<EnrolledSessionsContextType>({} as EnrolledSessionsContextType);

export const useEnrolledSessions = (): EnrolledSessionsContextType => useContext(EnrolledSessionsContext);

export function EnrolledSessionsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [enrolledSessions, setEnrolledSessions] = useState([]);

  const setEnrollment = (sessions: UpdateSession[]): void => {
    setEnrolledSessions((currentlyEnrolledSessions: UpdateSession['session'][]): UpdateSession['session'][] => {
      const newEnrolledSessions = [...currentlyEnrolledSessions];

      sessions.forEach(({ session, enrolling }) => {
        const sessionIndex = newEnrolledSessions.findIndex(s => s.id === session.id);
        if (sessionIndex === -1) {
          newEnrolledSessions.push({
            ...session,
            enrolled: enrolling,
            capacity: session?.capacity && {
              ...session.capacity,
              availableCapacity: enrolling
                ? session.capacity.availableCapacity - 1
                : session.capacity.availableCapacity + 1
            }
          });
        } else {
          newEnrolledSessions[sessionIndex] = {
            ...newEnrolledSessions[sessionIndex],
            enrolled: enrolling,
            capacity: session?.capacity && {
              ...session.capacity,
              availableCapacity: enrolling
                ? session.capacity.availableCapacity - 1
                : session.capacity.availableCapacity + 1
            }
          };
        }
      });

      return newEnrolledSessions;
    });
  };

  const value = useMemo(
    () => ({
      enrolledSessions,
      setEnrollment
    }),
    [enrolledSessions]
  );

  return <EnrolledSessionsContext.Provider value={value}>{children}</EnrolledSessionsContext.Provider>;
}

export default EnrolledSessionsContext;
